import React, { useEffect, useState } from "react";
import { Dialog } from "@primer/react/drafts";
import {
  Box,
  Text,
  TextInput,
  FormControl,
  Button,
  ButtonGroup,
  Select,
  ToggleSwitch,
} from "@primer/react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { monThiApi } from "../../api/monThiApi";
import { thangDiemApi } from "../../api/thangDiemApi";
import { ex_monthi } from "../../models/response/mon-thi/ex_monthi";
import { useCommonContext } from "../../contexts/common";
import { NotifyHelper } from "../../helpers/toast";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";

interface ThangDiem {
  id: number;
  thang_diem: string;
  dm_coso_id: number;
}

interface MonThiDetailModalProps {
  id: number;
  onSuccess: () => void;
  onCancel: () => void;
}

interface ApiResponse {
  is_success: boolean;
  data?: ex_monthi;
  message?: string;
}

const MonThiDetailModal: React.FC<MonThiDetailModalProps> = ({
  id,
  onSuccess,
  onCancel,
}) => {
  const { translate } = useCommonContext();
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
  const [isSaving, setIsSaving] = useState(false);
  const [thangDiems, setThangDiems] = useState<ThangDiem[]>([]);

  const defaultValues: ex_monthi = {
    id: id,
    mon_thi: "",
    mon_thi_en: "",
    ex_thangdiem_id: 0,
    nhan_xet_chung: false,
    dm_coso_id: dm_coso_id,
  };

  const { setValue, register, handleSubmit, reset, watch } = useForm<ex_monthi>(
    {
      defaultValues,
    }
  );

  const formValues = watch();

  const loadDetail = async () => {
    if (id > 0) {
      try {
        const res = (await monThiApi.detail(id)) as ApiResponse;
        if (res.is_success && res.data) {
          reset(res.data);
        }
      } catch (error) {
        NotifyHelper.Error(translate("Error.LoadDataFailed"));
      }
    }
  };

  const loadThangDiems = async () => {
    try {
      const res = await thangDiemApi.selectAll();
      if (res.is_success) {
        const filtered = res.data.filter(
          (x: ThangDiem) => x.dm_coso_id === dm_coso_id
        );
        setThangDiems(filtered);
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.LoadDataFailed"));
    }
  };

  useEffect(() => {
    loadDetail();
    loadThangDiems();
  }, [id]);

  const onSubmit = async (data: ex_monthi) => {
    if (data.ex_thangdiem_id === 0) {
      NotifyHelper.Error(translate("Vui lòng chọn thang điểm"));
      return;
    }

    setIsSaving(true);
    try {
      const api = id > 0 ? monThiApi.update : monThiApi.insert;
      const res = (await api(data)) as ApiResponse;

      if (res.is_success) {
        NotifyHelper.Success(
          translate(id > 0 ? "Success.Update" : "Success.Create")
        );
        onSuccess();
      } else {
        NotifyHelper.Error(res.message || translate("Error.Generic"));
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.Generic"));
    }
    setIsSaving(false);
  };

  const handleToggleChange = (e: React.MouseEvent) => {
    e.preventDefault();
    setValue("nhan_xet_chung", !formValues.nhan_xet_chung);
  };

  return (
    <Dialog
      title={translate(id > 0 ? "Base.Label.Update" : "Base.Label.Create")}
      onClose={onCancel}
      width="medium"
    >
      <Box as="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 0 }}>
        <FormControl sx={{ mb: 3 }}>
          <FormControl.Label>{translate("Môn thi")}</FormControl.Label>
          <TextInput {...register("mon_thi", { required: true })} block />
        </FormControl>
        <FormControl sx={{ mb: 3 }}>
          <FormControl.Label>{translate("Môn thi (En)")}</FormControl.Label>
          <TextInput {...register("mon_thi_en", { required: true })} block />
        </FormControl>
       
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 3,
            mb: 3,
            alignItems: "center",
          }}
        >
          <FormControl>
            <FormControl.Label>{translate("Thang điểm")}</FormControl.Label>
            <Select
              {...register("ex_thangdiem_id")}
              value={formValues.ex_thangdiem_id.toString()}
              onChange={(e) =>
                setValue("ex_thangdiem_id", parseInt(e.target.value))
              }
            >
              <Select.Option value="0">
                {translate("Chọn thang điểm")}
              </Select.Option>
              {thangDiems
                .filter(
                  (item) => item.thang_diem && item.thang_diem.trim() !== ""
                )
                .map((item) => (
                  <Select.Option key={item.id} value={item.id.toString()}>
                    {item.thang_diem}
                  </Select.Option>
                ))}
            </Select>
          </FormControl>

          <FormControl sx={{ pt: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <input
                type="checkbox"
                id="nhan-xet-chung-checkbox"
                checked={formValues.nhan_xet_chung}
                onChange={(e) => setValue("nhan_xet_chung", e.target.checked)}
              />
              <FormControl.Label
                htmlFor="nhan-xet-chung-checkbox"
                sx={{ mb: 0 }}
              >
                {translate("Nhận xét chung")}
              </FormControl.Label>
            </Box>
          </FormControl>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <ButtonGroup>
            <Button onClick={onCancel}>{translate("Base.Label.Cancel")}</Button>
            <Button variant="primary" type="submit" disabled={isSaving}>
              {translate(id > 0 ? "Base.Label.Update" : "Base.Label.Create")}
            </Button>
          </ButtonGroup>
        </Box>
      </Box>
    </Dialog>
  );
};

export default MonThiDetailModal;
